/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import { Popover } from 'antd'
import colors from '@sportninja/common/constants/appColors'

const POPOVER_OVERLAY_STYLE = { zIndex: 1060 }
const POPOVER_INNER_STYLE = {
  background: colors.SECONDARY_300,
  border: `1px solid ${colors.SECONDARY_100}`,
  borderRadius: '8px',
  padding: 0,
}

export interface CalendarGameViolation {
  constraintName: string
  message: string
  suggestion: string | null
  severity: 'error' | 'warning' | 'info'
}

export interface CalendarGame {
  id: string
  date: Date
  time: string
  homeTeam: string
  awayTeam: string
  venue: string
  divisionColor: string
  // Fields needed for inline editing
  homeTeamId: string
  awayTeamId: string
  locationId: string
  surfaceId: string
  divisionId: string
  startsAt: string
  endsAt: string
  approvalStatus: string
  violations?: CalendarGameViolation[]
  gameNumber?: number
}

interface CalendarGameCardProps {
  game: CalendarGame
  onClick?: (game: CalendarGame) => void
}

/**
 * Calendar Game Card Component
 * Displays a single game within a calendar day cell
 */
const APPROVED_COLOR = '#10B981'

const WARNING_COLOR = '#F59E0B'

const CalendarGameCard: React.FC<CalendarGameCardProps> = ({
  game,
  onClick,
}) => {
  const [violationPopoverOpen, setViolationPopoverOpen] = useState(false)
  const isApproved = game.approvalStatus === 'approved'
  const accentColor = isApproved ? APPROVED_COLOR : game.divisionColor
  const hasViolations = game.violations && game.violations.length > 0

  const cardStyles = css`
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 5px 8px;
    background: ${colors.SECONDARY_400};
    border-radius: 4px;
    border-left: 3px solid ${accentColor};
    cursor: pointer;
    transition: background 0.2s ease;
    min-width: 0;

    &:hover {
      background: ${colors.SECONDARY_300};
    }
  `

  const timeRowStyles = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
  `

  const timeStyles = css`
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 12px;
    line-height: 15px;
    color: ${accentColor};
    margin: 0;
  `

  const cautionIconStyles = css`
    flex-shrink: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 2px;
    transition: background 0.15s ease;

    &:hover {
      background: ${colors.SECONDARY_200};
    }

    svg {
      width: 12px;
      height: 12px;
    }
  `

  const matchupStyles = css`
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 15px;
    color: ${colors.WHITE};
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `

  const venueTagStyles = css`
    display: inline-flex;
    align-items: center;
    padding: 1px 4px;
    background: ${colors.SECONDARY_500};
    border-radius: 2px;
    margin-top: 2px;
    max-width: fit-content;
  `

  const venueTextStyles = css`
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 10px;
    line-height: 13px;
    color: ${colors.NEUTRAL_70};
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 120px;
  `

  const handleClick = () => {
    onClick?.(game)
  }

  const handleCautionClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setViolationPopoverOpen((prev) => !prev)
  }

  const violationPopoverContent = hasViolations ? (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px;
        max-width: 300px;
        max-height: 250px;
        overflow-y: auto;
      `}
    >
      <p
        css={css`
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 12px;
          color: ${WARNING_COLOR};
          margin: 0;
        `}
      >
        Violations ({game.violations!.length})
      </p>
      {game.violations!.map((v, i) => (
        <div
          key={i}
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            padding: 8px;
            background: ${colors.SECONDARY_500};
            border-radius: 4px;
            border-left: 3px solid
              ${v.severity === 'error' ? '#EF4444' : WARNING_COLOR};
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 6px;
            `}
          >
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 600;
                font-size: 9px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                padding: 1px 4px;
                border-radius: 2px;
                background: ${v.severity === 'error'
                  ? '#EF444420'
                  : `${WARNING_COLOR}20`};
                color: ${v.severity === 'error' ? '#EF4444' : WARNING_COLOR};
              `}
            >
              {v.severity}
            </span>
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 500;
                font-size: 10px;
                color: ${colors.WHITE};
              `}
            >
              {v.constraintName}
            </span>
          </div>
          <p
            css={css`
              font-family: 'Inter', sans-serif;
              font-weight: 400;
              font-size: 10px;
              line-height: 14px;
              color: ${colors.NEUTRAL_70};
              margin: 0;
            `}
          >
            {v.message}
          </p>
          {v.suggestion && (
            <p
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 400;
                font-size: 10px;
                line-height: 14px;
                color: ${colors.NEUTRAL_50};
                margin: 0;
                font-style: italic;
              `}
            >
              {v.suggestion}
            </p>
          )}
        </div>
      ))}
    </div>
  ) : null

  const cautionIcon = hasViolations ? (
    <Popover
      content={violationPopoverContent}
      trigger='click'
      open={violationPopoverOpen}
      onOpenChange={(visible) => setViolationPopoverOpen(visible)}
      placement='right'
      overlayStyle={POPOVER_OVERLAY_STYLE}
      overlayInnerStyle={POPOVER_INNER_STYLE}
      destroyTooltipOnHide
    >
      <span css={cautionIconStyles} onClick={handleCautionClick}>
        <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M12 2L1 21h22L12 2z'
            fill={WARNING_COLOR}
            stroke={WARNING_COLOR}
            strokeWidth='1'
          />
          <text
            x='12'
            y='18'
            textAnchor='middle'
            fontSize='12'
            fontWeight='bold'
            fill={colors.SECONDARY_500}
          >
            !
          </text>
        </svg>
      </span>
    </Popover>
  ) : null

  return (
    <div css={cardStyles} onClick={handleClick}>
      <div css={timeRowStyles}>
        <p css={timeStyles}>{game.time}</p>
        {cautionIcon}
      </div>
      <p css={matchupStyles}>
        {game.homeTeam} vs {game.awayTeam}
      </p>
      <div css={venueTagStyles}>
        <p css={venueTextStyles}>{game.venue}</p>
      </div>
    </div>
  )
}

export default CalendarGameCard
